import { API_BASE } from './apiBase';
import { TELEMETRY_MAX_BATCH, type TelemetryEvent, type TelemetryEventType } from '@brainfart/shared';

export type { TelemetryEventType };

// Stored by the game server in a D1 database.
const ENDPOINT = `${API_BASE}/api/telemetry`;

const QUEUE_KEY = 'telemetry_queue';
const MAX_QUEUE = 500;
const FLUSH_INTERVAL = 10000;

let flushTimer: ReturnType<typeof setTimeout> | null = null;

function getQueue(): TelemetryEvent[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveQueue(queue: TelemetryEvent[]) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue.slice(-MAX_QUEUE)));
  } catch {
    // ignore quota errors
  }
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush();
  }, FLUSH_INTERVAL);
}

function storageId(storage: Storage, key: string): string | undefined {
  try {
    let id = storage.getItem(key);
    if (!id) {
      id = crypto.randomUUID();
      storage.setItem(key, id);
    }
    return id;
  } catch {
    return undefined;
  }
}

export function track(event: { type: TelemetryEventType; roomCode?: string; payload: Record<string, unknown> }) {
  const queue = getQueue();
  queue.push({
    ...event,
    timestamp: Date.now(),
    sessionId: storageId(sessionStorage, 'telemetry_session'),
    // Same anonymous id the multiplayer client uses, so repeat players can be counted.
    playerId: storageId(localStorage, 'bf_player_id'),
  });
  saveQueue(queue);
  scheduleFlush();
}

// text/plain keeps these "simple" cross-origin requests (no CORS preflight),
// and is the only type sendBeacon reliably allows cross-origin.
const body = (events: TelemetryEvent[]) => JSON.stringify({ events });

async function flush() {
  const queue = getQueue();
  if (queue.length === 0) return;

  const toSend = queue.splice(0, TELEMETRY_MAX_BATCH);
  saveQueue(queue);

  try {
    const res = await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: body(toSend),
      keepalive: true,
    });
    // Only drop a batch the server rejected as bad data. Anything else (404
    // before the endpoint is deployed, 5xx, rate limits) keeps it queued.
    if (!res.ok && res.status !== 400 && res.status !== 413) throw new Error(`telemetry ${res.status}`);
  } catch {
    saveQueue([...toSend, ...getQueue()]);
  }

  if (getQueue().length > 0) scheduleFlush();
}

// Last-chance send when the tab is hidden or closed (mobile browsers often
// skip beforeunload). Only clear what the browser accepted, so nothing is
// sent twice on the next visit.
function flushWithBeacon() {
  const queue = getQueue();
  if (queue.length === 0 || typeof navigator.sendBeacon !== 'function') return;
  const batch = queue.slice(0, TELEMETRY_MAX_BATCH);
  const sent = navigator.sendBeacon(ENDPOINT, new Blob([body(batch)], { type: 'text/plain' }));
  if (sent) saveQueue(queue.slice(batch.length));
}

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', flushWithBeacon);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushWithBeacon();
  });
  // Pick up anything queued on a previous visit.
  scheduleFlush();
}

// Convenience trackers
export function trackGameCreated(roomCode: string, categories: string[], themes: string[], isHost: boolean) {
  track({ type: 'game_created', roomCode, payload: { categories, themes, isHost } });
}

export function trackGameJoined(roomCode: string, playerCount: number) {
  track({ type: 'game_joined', roomCode, payload: { playerCount } });
}

export function trackGameStarted(roomCode: string, roundCount: number, playerCount: number, categories: string[]) {
  track({ type: 'game_started', roomCode, payload: { roundCount, playerCount, categories } });
}

export function trackGameEnded(roomCode: string, payload: { roundCount: number; playerCount: number; durationMs: number; correct: number; rank: number; categories: string[] }) {
  track({ type: 'game_ended', roomCode, payload });
}

// correct is null in multiplayer: the server only reveals it after the round.
export function trackQuestionAnswered(roomCode: string | undefined, questionId: string, category: string, correct: boolean | null, answerTimeMs: number) {
  track({ type: 'question_answered', roomCode, payload: { questionId, category, correct, answerTimeMs } });
}

export function trackQuestionTimedOut(roomCode: string, questionId: string, category: string) {
  track({ type: 'question_timed_out', roomCode, payload: { questionId, category } });
}

export function trackCategorySelected(roomCode: string, categories: string[], availableCount: number) {
  track({ type: 'category_selected', roomCode, payload: { categories, availableCount } });
}

export function trackThemeSelected(roomCode: string, themes: string[]) {
  track({ type: 'theme_selected', roomCode, payload: { themes } });
}

export function trackSettingsChanged(roomCode: string, settings: Record<string, unknown>) {
  track({ type: 'settings_changed', roomCode, payload: settings });
}

export function trackPlayerLeft(roomCode: string, playerCount: number) {
  track({ type: 'player_left', roomCode, payload: { playerCount } });
}

export function trackDailyChallengeStarted(challengeNumber: number) {
  track({ type: 'daily_challenge_started', payload: { challengeNumber } });
}

export function trackDailyChallengeCompleted(challengeNumber: number, score: number, correct: number, total: number) {
  track({ type: 'daily_challenge_completed', payload: { challengeNumber, score, correct, total } });
}

export function trackSoloChallengeStarted(seed: number, roundCount: number, categories: string[]) {
  track({ type: 'solo_challenge_started', payload: { seed, roundCount, categories } });
}

export function trackSoloChallengeCompleted(seed: number, score: number, correct: number, total: number) {
  track({ type: 'solo_challenge_completed', payload: { seed, score, correct, total } });
}

export function trackSoloChallengeGaveUp(seed: number, questionIndex: number, score: number) {
  track({ type: 'solo_challenge_gave_up', payload: { seed, questionIndex, score } });
}

export function trackReactionSent(roomCode: string, emoji: string) {
  track({ type: 'reaction_sent', roomCode, payload: { emoji } });
}

export function trackShareClicked(roomCode: string | undefined, shareType: 'daily' | 'multiplayer' | 'solo') {
  track({ type: 'share_clicked', roomCode, payload: { shareType } });
}

export function trackProfileViewed() {
  track({ type: 'profile_viewed', payload: {} });
}

export function trackLeaderboardViewed() {
  track({ type: 'leaderboard_viewed', payload: {} });
}

export function trackSettingsOpened() {
  track({ type: 'settings_opened', payload: {} });
}

export function trackCategoryFilterChanged(categories: string[]) {
  track({ type: 'category_filter_changed', payload: { categories } });
}

export function trackRematchStarted(roomCode: string, playerCount: number) {
  track({ type: 'rematch_started', roomCode, payload: { playerCount } });
}

export function trackFeedbackClicked(from: string) {
  track({ type: 'feedback_clicked', payload: { from } });
}

// Once per page load; `standalone` means it was launched from the home screen.
export function trackAppOpened(standalone: boolean, source: string | null) {
  track({ type: 'app_opened', payload: { standalone, source } });
}

export function trackInstallPrompt(stage: 'shown' | 'clicked' | 'dismissed', platform: 'android' | 'ios', where: string) {
  track({ type: `install_prompt_${stage}`, payload: { platform, where } });
}

export function trackAppInstalled() {
  track({ type: 'app_installed', payload: {} });
}

export function trackReminder(stage: 'prompt_shown' | 'enabled' | 'denied' | 'dismissed' | 'disabled', where: string) {
  track({ type: `reminder_${stage}`, payload: { where } });
}
